import { useState, Fragment } from 'react';
import {
  useGetStepRuns,
  useGetChildWorkflowRuns,
  useRetryStep,
} from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Textarea } from 'src/components/ui/textarea';
import { Label } from 'src/components/ui/label';
import {
  CircleNotchIcon,
  ArrowsClockwiseIcon,
  CaretDownIcon,
  CaretRightIcon,
  GitBranchIcon,
} from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import type { StepRunResponse, WorkflowRunResponse } from '@erp/rpc';
import { useToast } from 'src/context/ToastContext';
import { StepArtifactsDisplay } from './StepArtifactsDisplay';
import { FLOW_IN_SOCKET_ID, FLOW_OUT_SOCKET_ID } from '@erp/workflows';

interface WorkflowRunStepsListProps {
  workflowRunId: string;
  stepNames: Map<string, string>;
  stepTypes: Map<string, string>;
  onRefresh?: () => void;
}

interface GroupedStepRuns {
  stepDefinitionId: string;
  latestAttempt: StepRunResponse;
  previousAttempts: StepRunResponse[];
}

function formatDuration(
  startedAt?: string,
  completedAt?: string,
): string | null {
  if (!startedAt || !completedAt) {
    return null;
  }
  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  const durationMs = end - start;
  const seconds = durationMs / 1000;
  return seconds.toFixed(3);
}

function getStatusVariant(
  status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'completed':
      return 'default';
    case 'failed':
    case 'timed_out':
      return 'destructive';
    case 'running':
      return 'secondary';
    case 'cancelled':
      return 'outline';
    default:
      return 'outline';
  }
}

function StepRunRowContent({
  step,
  stepName,
  isHistorical,
}: {
  step: StepRunResponse;
  stepName: string;
  isHistorical?: boolean;
}) {
  return (
    <>
      <TableCell className={isHistorical ? 'pl-10' : ''}>
        <div className="flex items-center gap-2">
          <span className={isHistorical ? 'text-muted-foreground' : ''}>
            {stepName}
          </span>
          <Badge variant="outline" className="text-xs">
            #{step.attemptNumber}
          </Badge>
        </div>
      </TableCell>
      <TableCell>
        <Badge variant={getStatusVariant(step.status)}>{step.status}</Badge>
      </TableCell>
      <TableCell>
        {step.startedAt ? (
          <span
            className={`text-sm ${isHistorical ? 'text-muted-foreground' : ''}`}
          >
            {formatDistanceToNow(new Date(step.startedAt), {
              addSuffix: true,
            })}
          </span>
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell className="text-right font-mono">
        {formatDuration(step.startedAt, step.completedAt) || '-'}
      </TableCell>
      <TableCell>
        {step.stopReason ? (
          <span
            className={`text-sm line-clamp-2 ${isHistorical ? 'text-muted-foreground' : 'text-destructive'}`}
          >
            {step.stopReason}
          </span>
        ) : (
          '-'
        )}
      </TableCell>
    </>
  );
}

interface StepRowGroupProps {
  group: GroupedStepRuns;
  stepName: string;
  stepType: string | undefined;
  onRetry: (step: StepRunResponse) => void;
}

function hasNonFlowArtifacts(
  data: Record<string, unknown> | undefined,
): boolean {
  if (!data) return false;
  return Object.keys(data).some(
    (k) => k !== FLOW_IN_SOCKET_ID && k !== FLOW_OUT_SOCKET_ID,
  );
}

function StepRowGroup({
  group,
  stepName,
  stepType,
  onRetry,
}: StepRowGroupProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { latestAttempt, previousAttempts } = group;
  const hasHistory = previousAttempts.length > 0;

  const canRetry =
    latestAttempt.status === 'failed' || latestAttempt.status === 'timed_out';

  const isEventTrigger = stepType === 'event_trigger';
  const inputDataToShow = isEventTrigger ? undefined : latestAttempt.inputData;

  const hasArtifacts =
    hasNonFlowArtifacts(inputDataToShow) ||
    hasNonFlowArtifacts(latestAttempt.outputData);

  const isExpandable = hasHistory || hasArtifacts;

  return (
    <Fragment>
      <TableRow
        className={isExpandable ? 'cursor-pointer hover:bg-muted/50' : ''}
        onClick={() => isExpandable && setIsExpanded(!isExpanded)}
      >
        <TableCell className="w-8">
          {isExpandable ? (
            <Button
              variant="ghost"
              size="sm"
              className="p-0 h-6 w-6"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
            >
              {isExpanded ? (
                <CaretDownIcon className="h-4 w-4" />
              ) : (
                <CaretRightIcon className="h-4 w-4" />
              )}
            </Button>
          ) : null}
        </TableCell>
        <StepRunRowContent step={latestAttempt} stepName={stepName} />
        <TableCell>
          {canRetry && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onRetry(latestAttempt);
              }}
              title="Retry step"
            >
              <ArrowsClockwiseIcon className="h-4 w-4" />
            </Button>
          )}
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow>
          <TableCell colSpan={7} className="p-0 bg-muted/20">
            <div className="p-4 space-y-4">
              {hasArtifacts && (
                <StepArtifactsDisplay
                  inputData={inputDataToShow}
                  outputData={latestAttempt.outputData}
                />
              )}
              {hasHistory && (
                <div>
                  <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Previous Attempts
                  </h5>
                  <Table>
                    <TableBody>
                      {previousAttempts.map((attempt) => (
                        <TableRow key={attempt.id} className="bg-muted/10">
                          <TableCell className="w-8" />
                          <StepRunRowContent
                            step={attempt}
                            stepName={stepName}
                            isHistorical
                          />
                          <TableCell />
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}

interface ChildWorkflowRowProps {
  childRun: WorkflowRunResponse;
  index: number;
  stepNames: Map<string, string>;
}

function ChildWorkflowRow({
  childRun,
  index,
  stepNames,
}: ChildWorkflowRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data: stepsData, isLoading } = useGetStepRuns(
    isExpanded ? childRun.id : undefined,
  );

  const parentContext = childRun.parentContext as
    | { itemIndex?: number; totalItems?: number }
    | undefined;
  const itemIndex = parentContext?.itemIndex ?? index;
  const totalItems = parentContext?.totalItems ?? '?';

  return (
    <div className="border rounded-md mb-2">
      <div
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Button variant="ghost" size="sm" className="p-0 h-6 w-6">
          {isExpanded ? (
            <CaretDownIcon className="h-4 w-4" />
          ) : (
            <CaretRightIcon className="h-4 w-4" />
          )}
        </Button>
        <GitBranchIcon className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">
          Invoice {itemIndex + 1} of {totalItems}
        </span>
        <Badge variant={getStatusVariant(childRun.status)}>
          {childRun.status}
        </Badge>
        <span className="text-sm text-muted-foreground ml-auto">
          {childRun.completedSteps}/{childRun.totalSteps} steps
        </span>
      </div>
      {isExpanded && (
        <div className="p-3 pt-0 border-t">
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <CircleNotchIcon className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : stepsData?.stepRuns && stepsData.stepRuns.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Step</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead className="text-right">Duration (sec)</TableHead>
                  <TableHead>Stop Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stepsData.stepRuns.map((step) => (
                  <TableRow key={step.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span>
                          {stepNames.get(step.stepDefinitionId) ||
                            step.stepDefinitionId.slice(0, 8) + '...'}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          #{step.attemptNumber}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(step.status)}>
                        {step.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {step.startedAt ? (
                        <span className="text-sm">
                          {formatDistanceToNow(new Date(step.startedAt), {
                            addSuffix: true,
                          })}
                        </span>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatDuration(step.startedAt, step.completedAt) || '-'}
                    </TableCell>
                    <TableCell>
                      {step.stopReason ? (
                        <span className="text-sm text-destructive line-clamp-2">
                          {step.stopReason}
                        </span>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-2">
              No step runs yet.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

interface ChildWorkflowsSectionProps {
  childWorkflows: WorkflowRunResponse[];
  stepNames: Map<string, string>;
}

function ChildWorkflowsSection({
  childWorkflows,
  stepNames,
}: ChildWorkflowsSectionProps) {
  if (childWorkflows.length === 0) return null;

  const completedCount = childWorkflows.filter(
    (c) => c.status === 'completed',
  ).length;
  const failedCount = childWorkflows.filter(
    (c) => c.status === 'failed',
  ).length;

  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 mb-3">
        <GitBranchIcon className="h-5 w-5 text-muted-foreground" />
        <h3 className="text-lg font-semibold">
          Processing {childWorkflows.length} Invoices
        </h3>
        <span className="text-sm text-muted-foreground">
          ({completedCount} completed
          {failedCount > 0 && `, ${failedCount} failed`})
        </span>
      </div>
      <div className="space-y-2">
        {childWorkflows.map((childRun, index) => (
          <ChildWorkflowRow
            key={childRun.id}
            childRun={childRun}
            index={index}
            stepNames={stepNames}
          />
        ))}
      </div>
    </div>
  );
}

export function WorkflowRunStepsList({
  workflowRunId,
  stepNames,
  stepTypes,
  onRefresh,
}: WorkflowRunStepsListProps) {
  const { data: stepsData, isLoading, refetch } = useGetStepRuns(workflowRunId);
  const { data: childWorkflowsData } = useGetChildWorkflowRuns(workflowRunId);
  const retryStepMutation = useRetryStep();
  const { showToast } = useToast();

  const [selectedStep, setSelectedStep] = useState<StepRunResponse | null>(
    null,
  );
  const [editedInputData, setEditedInputData] = useState<string>('');
  const [isRetryDialogOpen, setIsRetryDialogOpen] = useState(false);

  function handleOpenRetryDialog(step: StepRunResponse) {
    setSelectedStep(step);
    setEditedInputData(JSON.stringify(step.inputData, null, 2));
    setIsRetryDialogOpen(true);
  }

  function handleCloseDialog() {
    setSelectedStep(null);
    setIsRetryDialogOpen(false);
    setEditedInputData('');
  }

  async function handleRetry() {
    if (!selectedStep) return;

    let inputData: Record<string, unknown> | undefined;
    try {
      inputData = JSON.parse(editedInputData);
    } catch {
      showToast({
        title: 'Invalid JSON',
        message: 'Please enter valid JSON for the input data.',
        color: 'error',
      });
      return;
    }

    try {
      await retryStepMutation.mutateAsync({
        workflowRunId,
        stepRunId: selectedStep.id,
        inputData,
      });

      showToast({
        title: 'Step retry initiated',
        message: 'The step has been queued for retry.',
        color: 'success',
      });

      handleCloseDialog();
      refetch();
      onRefresh?.();
    } catch (error) {
      showToast({
        title: 'Retry failed',
        message:
          error instanceof Error
            ? error.message
            : 'An error occurred while retrying the step.',
        color: 'error',
      });
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <CircleNotchIcon className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const stepRuns = stepsData?.stepRuns || [];

  // Group step runs by step definition, keeping all attempts
  const groupedSteps = stepRuns.reduce((acc, step) => {
    const existing = acc.get(step.stepDefinitionId);
    if (!existing) {
      acc.set(step.stepDefinitionId, [step]);
    } else {
      existing.push(step);
    }
    return acc;
  }, new Map<string, StepRunResponse[]>());

  // Convert to array of GroupedStepRuns, sorted by attempt number (descending)
  const stepGroups: GroupedStepRuns[] = Array.from(groupedSteps.entries()).map(
    ([stepDefinitionId, attempts]) => {
      // Sort by attemptNumber descending (latest first)
      const sorted = [...attempts].sort(
        (a, b) => b.attemptNumber - a.attemptNumber,
      );
      return {
        stepDefinitionId,
        latestAttempt: sorted[0],
        previousAttempts: sorted.slice(1),
      };
    },
  );

  if (stepGroups.length === 0) {
    return (
      <p className="text-muted-foreground text-center py-4 text-sm">
        No step runs recorded yet.
      </p>
    );
  }

  const childWorkflows = childWorkflowsData?.childWorkflowRuns || [];

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead>Step</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Started</TableHead>
            <TableHead className="text-right">Duration (sec)</TableHead>
            <TableHead>Stop Reason</TableHead>
            <TableHead className="w-[100px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {stepGroups.map((group) => (
            <StepRowGroup
              key={group.stepDefinitionId}
              group={group}
              stepName={
                stepNames.get(group.stepDefinitionId) ||
                group.stepDefinitionId.slice(0, 8) + '...'
              }
              stepType={stepTypes.get(group.stepDefinitionId)}
              onRetry={handleOpenRetryDialog}
            />
          ))}
        </TableBody>
      </Table>

      {childWorkflows.length > 0 && (
        <ChildWorkflowsSection
          childWorkflows={childWorkflows}
          stepNames={stepNames}
        />
      )}

      <Dialog
        open={isRetryDialogOpen}
        onOpenChange={(open) => !open && handleCloseDialog()}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Retry Step</DialogTitle>
            <DialogDescription>
              Retry this failed step with optional modified input data.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="inputData">Input Data (JSON)</Label>
              <Textarea
                id="inputData"
                value={editedInputData}
                onChange={(e) => setEditedInputData(e.target.value)}
                className="font-mono text-sm min-h-[200px]"
                placeholder='{"key": "value"}'
              />
              <p className="text-xs text-muted-foreground">
                Edit the JSON above to modify the input data for this step.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleRetry}
              disabled={retryStepMutation.isPending}
            >
              {retryStepMutation.isPending && (
                <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
              )}
              Retry Step
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
